import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "../i18n";
import PageShell from "../components/PageShell";
import ConfirmDialog from "../components/ConfirmDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import CampaignTable from "../components/campaign/CampaignTable";
import CampaignLog from "../components/campaign/CampaignLog";
import CampaignRunProgress from "../components/campaign/CampaignRunProgress";
import CampaignFormDialog from "../components/campaign/CampaignFormDialog";
import SegmentManagerDialog from "../components/campaign/SegmentManagerDialog";
import CreateMenu from "../components/campaign/CreateMenu";
import {
  useCampaigns,
  useTriggerTypes,
  useCampaignLog,
  useCampaignStats,
  useCampaignRunState,
  useCreateCampaign,
  useUpdateCampaign,
  useDeleteCampaign,
  useRunCampaign,
  useStopCampaign,
  useTestCampaign,
  useSegmentDefinitions,
} from "../features/super/hooks/useCampaigns";
import toast from "react-hot-toast";

/**
 * Query parameter holding the campaign in focus.
 *
 * Selection used to live in component state, so a reload (or a shared link)
 * always landed on an empty dashboard with the operator's campaign closed.
 * The id belongs in the URL for the same reason the log offset does not: it is
 * what the page is currently about.
 */
const CAMPAIGN_PARAM = "campaign";

export default function CampaignDashboard() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Data via tanstack
  const {
    data: campaigns = [],
    isError: campaignsFailed,
    error: campaignsError,
    refetch: refetchCampaigns,
  } = useCampaigns();
  const { data: triggerTypes = [] } = useTriggerTypes();
  const createMutation = useCreateCampaign();
  const updateMutation = useUpdateCampaign();
  const deleteMutation = useDeleteCampaign();
  const runMutation = useRunCampaign();
  const stopMutation = useStopCampaign();
  const testMutation = useTestCampaign();

  // UI state
  const selectedId = campaignIdFromParams(searchParams);
  const [busyId, setBusyId] = useState(null);
  const [logOffset, setLogOffset] = useState(0);

  // Dialogs. `editing` holds the campaign open in the form dialog; null with
  // `formOpen` means a new one is being created.
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [segmentsOpen, setSegmentsOpen] = useState(false);
  // Campaign the segment manager is scoped to: null lists and creates the
  // global definitions, an id lists and creates that campaign's own. The
  // header "Segments" button scopes it to the campaign in focus, the
  // Create-menu item leaves it global — without the id the operator would have
  // to open the manager for a campaign to reach the segments stored against
  // it, and `GET /campaigns/segments` without a `campaign_id` answers with
  // the `campaign_id IS NULL` rows alone.
  const [segmentsCampaignId, setSegmentsCampaignId] = useState(null);

  const openCreateForm = useCallback(() => {
    setEditingId(null);
    setFormOpen(true);
  }, []);

  const openEditForm = useCallback((id) => {
    setEditingId(id);
    setFormOpen(true);
  }, []);

  const openSegments = useCallback((campaignId) => {
    setSegmentsCampaignId(campaignId);
    setSegmentsOpen(true);
  }, []);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Test campaign dialog
  const [testTarget, setTestTarget] = useState(null);
  const [testPhone, setTestPhone] = useState("");

  const selectedCampaign = campaigns.find((c) => c.id === selectedId) || null;
  const editingCampaign = campaigns.find((c) => c.id === editingId) || null;

  const selectCampaign = useCallback(
    (id) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id == null) next.delete(CAMPAIGN_PARAM);
          else next.set(CAMPAIGN_PARAM, String(id));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // A finished run changes the numbers the stats/log panels show.
  const { data: logData, isError: logFailed } = useCampaignLog(
    selectedId,
    logOffset,
  );
  const { data: statsData } = useCampaignStats(selectedId);
  const { data: runState } = useCampaignRunState(selectedId);
  // Custom segment names for the `Имя сегмента` picker. Scoped to the campaign
  // the form is editing, not to the one the URL happens to point at — those
  // were the same campaign only while the operator had not navigated.
  const { data: segmentData } = useSegmentDefinitions(editingCampaign?.id);
  const customSegmentNames = (segmentData?.items ?? []).map((s) => s.name);

  // Selecting another campaign starts its log from the newest page.
  useEffect(() => {
    setLogOffset(0);
  }, [selectedId]);

  const withBusy = async (id, action) => {
    setBusyId(id);
    try {
      await action();
    } finally {
      setBusyId((current) => (current === id ? null : current));
    }
  };

  const handleCreate = async (data) => {
    const created = await createMutation.mutateAsync(data);
    toast.success(t("campaign.toastCreated"));
    // Land the operator on what they just made instead of on a list they have
    // to find the new row in.
    selectCampaign(created.id);
  };

  const handleSave = async (id, data) => {
    await updateMutation.mutateAsync({ id, data });
    toast.success(t("campaign.toastUpdated"));
  };

  const handleToggle = (id, enabled) =>
    withBusy(id, async () => {
      const campaign = campaigns.find((c) => c.id === id);
      if (!campaign) return;
      try {
        await updateMutation.mutateAsync({
          id,
          data: { config: campaign.config, enabled, name: campaign.name },
        });
      } catch (err) {
        toast.error(describeError(err, t("campaign.toastToggleFailed")));
      }
    });

  const handleRun = (id) =>
    withBusy(id, async () => {
      try {
        await runMutation.mutateAsync(id);
        toast.success(t("campaign.toastRunStarted"));
      } catch (err) {
        toast.error(describeError(err, t("campaign.toastRunFailed")));
      }
    });

  const handleStop = (id) =>
    withBusy(id, async () => {
      try {
        await stopMutation.mutateAsync(id);
        toast.success(t("campaign.toastStopRequested"));
      } catch (err) {
        toast.error(describeError(err, t("campaign.toastStopFailed")));
      }
    });

  const handleTest = (id) => {
    setTestTarget(id);
    setTestPhone("");
  };

  const handleTestSend = async () => {
    if (!testTarget || !testPhone.trim()) return;
    try {
      const result = await testMutation.mutateAsync({
        id: testTarget,
        phone: testPhone.trim(),
      });
      if (result.delivered > 0) {
        toast.success(
          t("campaign.toastTestResult", { name: result.customerName }),
        );
      } else {
        // A "successful" test that reached no device is the case that used to
        // report success while nothing was delivered.
        toast.error(
          t("campaign.toastTestNotDelivered", { name: result.customerName }),
        );
      }
      setTestTarget(null);
      setTestPhone("");
    } catch (err) {
      toast.error(describeError(err, t("campaign.toastTestFailed")));
    }
  };

  const handleDelete = () => {
    const id = deleteTarget;
    setDeleteTarget(null);
    return withBusy(id, async () => {
      try {
        await deleteMutation.mutateAsync(id);
        if (selectedId === id) selectCampaign(null);
        toast.success(t("campaign.toastDeleted"));
      } catch (err) {
        toast.error(describeError(err, t("campaign.toastDeleteFailed")));
      }
    });
  };

  const getLabel = (triggerType) => {
    const found = triggerTypes.find((tt) => tt.triggerType === triggerType);
    return found?.label || triggerType;
  };

  const deleteTargetName =
    deleteTarget != null
      ? campaigns.find((c) => c.id === deleteTarget)?.name || `#${deleteTarget}`
      : "";

  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <PageShell title={t("page.marketing")}>
      <div className="flex flex-col gap-4 h-full">
        {/* ── Campaign list ── */}
        <div className="w-full shrink-0">
          <div className="bg-c-gray-600 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-xl">
                {t("campaign.count", { count: campaigns.length })}
              </h2>
              <div className="flex items-center gap-2">
                {/* "Segments" reaches the definitions stored against the
                    campaign in focus — the ones this campaign's own picker
                    lists — while the Create menu's segment item opens the same
                    manager globally. Two entry points, two scopes, each one
                    stated by the control that opens it. */}
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => openSegments(selectedId ?? null)}
                >
                  {t("campaign.segmentsBtn")}
                </Button>
                <CreateMenu
                  items={[
                    {
                      key: "campaign",
                      label: t("campaign.createTitle"),
                      description: t("campaign.createMenuCampaign"),
                      onSelect: openCreateForm,
                    },
                    {
                      key: "segment",
                      label: t("campaign.segmentMenuTitle"),
                      description: t("campaign.createMenuSegment"),
                      onSelect: () => openSegments(null),
                    },
                  ]}
                />
              </div>
            </div>

            {campaignsFailed && (
              <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm flex items-center justify-between gap-3">
                <span>
                  {describeError(campaignsError, t("campaign.toastLoadFailed"))}
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={refetchCampaigns}
                >
                  {t("common.retry")}
                </Button>
              </div>
            )}

            <CampaignTable
              campaigns={campaigns}
              triggerTypes={triggerTypes}
              selectedId={selectedId}
              busyId={busyId}
              onSelect={selectCampaign}
              onEdit={openEditForm}
              onToggle={handleToggle}
              onRun={handleRun}
              onStop={handleStop}
              onTest={handleTest}
              onDelete={(id) => setDeleteTarget(id)}
            />
          </div>
        </div>

        {/* ── Selected campaign ── */}
        {selectedCampaign && (
          <div className="w-full shrink-0">
            <div className="bg-c-gray-600 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-xl truncate">
                  {selectedCampaign.name ||
                    t("campaign.campaignFallback", { id: selectedId })}
                  {" — "}
                  {getLabel(selectedCampaign.triggerType)}
                </h2>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => openEditForm(selectedCampaign.id)}
                  >
                    {t("campaign.editBtn")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => selectCampaign(null)}
                  >
                    {t("campaign.close")}
                  </Button>
                </div>
              </div>

              {runState && (
                <CampaignRunProgress run={runState} onStop={handleStop} />
              )}

              {statsData && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-c-gray-500/50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold">
                      {statsData.candidates}
                    </div>
                    <div className="text-c-gray-400 text-sm">
                      {t("campaign.statCandidates")}
                    </div>
                  </div>
                  <div className="bg-c-gray-500/50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-green-400">
                      {statsData.sent}
                    </div>
                    <div className="text-c-gray-400 text-sm">
                      {t("campaign.statSent")}
                    </div>
                  </div>
                  <div className="bg-c-gray-500/50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-red-400">
                      {statsData.failed}
                    </div>
                    <div className="text-c-gray-400 text-sm">
                      {t("campaign.statFailed")}
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <h3 className="text-lg">{t("campaign.log")}</h3>
                {logFailed ? (
                  <div className="text-red-400 text-sm py-4 text-center">
                    {t("campaign.toastLogFailed")}
                  </div>
                ) : (
                  <CampaignLog
                    log={logData}
                    offset={logOffset}
                    onOffsetChange={setLogOffset}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Campaign create / edit ── */}
      <CampaignFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        campaign={editingCampaign}
        triggerTypes={triggerTypes}
        segmentOptions={customSegmentNames}
        saving={saving}
        onCreate={handleCreate}
        onUpdate={handleSave}
      />

      {/* ── Segments ── */}
      <SegmentManagerDialog
        open={segmentsOpen}
        onOpenChange={setSegmentsOpen}
        campaignId={segmentsCampaignId}
      />

      {/* ── Delete confirmation ── */}
      <ConfirmDialog
        open={deleteTarget != null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={t("campaign.confirmDeleteTitle")}
        itemName={deleteTargetName}
        message={t("campaign.confirmDeleteMessage", { item: deleteTargetName })}
        confirmText={t("common.delete")}
        onConfirm={handleDelete}
      />

      {/* ── Test campaign dialog ── */}
      <ConfirmDialog
        open={testTarget != null}
        onOpenChange={(open) => {
          if (!open) {
            setTestTarget(null);
            setTestPhone("");
          }
        }}
        title={t("campaign.testDialogTitle")}
        message={t("campaign.testDialogDesc")}
        customContent={
          <div className="space-y-2">
            <Label>{t("campaign.testDialogPhone")}</Label>
            <Input
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder={t("campaign.testDialogPhonePlaceholder")}
              disabled={testMutation.isPending}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !testMutation.isPending) {
                  handleTestSend();
                }
              }}
            />
          </div>
        }
        confirmText={t("campaign.testDialogSend")}
        cancelText={t("campaign.testDialogCancel")}
        // Primary: sending a test is the dialog's affirmative action, and it
        // read as a disabled/secondary action while "secondary" was set.
        confirmVariant="default"
        onConfirm={handleTestSend}
        disabled={testMutation.isPending}
      />
    </PageShell>
  );
}

/**
 * The campaign id in the URL, or null when the parameter is absent or junk.
 *
 * A hand-edited `?campaign=abc` must not select the campaign with a NaN id —
 * the detail panels would render empty headers for a campaign that does not
 * exist, and every poll below would fire against it.
 */
function campaignIdFromParams(searchParams) {
  const raw = searchParams.get(CAMPAIGN_PARAM);
  if (raw == null || raw === "") return null;
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Surface the backend's error message instead of a bare "request failed".
 *
 * `apiFetch` callers throw `await resp.text()`, so the useful part — "Campaign
 * has 12 unfulfilled bonus promises" — was being caught and discarded by a
 * bare `catch {}`.
 */
function describeError(err, fallback) {
  if (typeof err === "string" && err.trim()) return err;
  if (err?.message) return err.message;
  return fallback;
}
